import type { FeedbackSubmission } from '@ctw/shared/api/feedback/feedback-manager';
import type { CtwFetchFn } from '@ctw/shared/context/ctw-context';
import { type Env, getBffApiBaseUrl, getStandaloneBaseUrl } from '@ctw/shared/utils/environments';

export type SubmitFeedbackSubmissionAttributes = Omit<
  FeedbackSubmission,
  'id' | 'submittedAt' | 'submitterName' | 'submitterEmail'
>;

export interface SubmitFeedbackSubmissionArgs {
  ctwFetch: CtwFetchFn;
  env: Env;
  authToken: string;
  attributes: SubmitFeedbackSubmissionAttributes;
  // When true, target the BFF API endpoint; otherwise the legacy standalone Next.js route.
  // Gated by the `ctw-bff-feedback` flag at the call site.
  useBffEndpoint: boolean;
}

/**
 * Posts a feedback submission to either the BFF API or the legacy standalone route. Both accept
 * the same JSON:API request body and expose an equivalent `ok`/`data` fetch result, so callers
 * keep their own success/error handling and only differ in which endpoint is targeted.
 */
export const submitFeedbackSubmission = ({
  ctwFetch,
  env,
  authToken,
  attributes,
  useBffEndpoint,
}: SubmitFeedbackSubmissionArgs): ReturnType<CtwFetchFn> => {
  const url = useBffEndpoint
    ? `${getBffApiBaseUrl(env)}/v1/feedback`
    : `${getStandaloneBaseUrl(env)}/api/feedback`;

  return ctwFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      data: {
        type: 'feedback-submission',
        attributes,
      },
    }),
  });
};
